import * as React from "react";
import { useParams, Link } from "react-router-dom";
import {
  Card,
  Descriptions,
  Tag,
  Space,
  Button,
  message,
  Skeleton,
  Select,
  Input,
} from "antd";
import { ArrowLeftOutlined, CloudDownloadOutlined, SaveOutlined } from "@ant-design/icons";
import { formatFullDateFromTimeStamp, SubmissionApiCommunicator } from "api";
import { downloadSubmissionOriginalFile } from "utils/downloadUtils";
import { getSubmissionStatusOptions, getStatusColor } from "utils/statusUtils";

const { TextArea } = Input;

/**
 * SubmissionDetailPage Component
 * 
 * Displays comprehensive information about a specific submission and allows status management.
 * Shows detailed metadata including nested part vector search data and provides
 * functionality to update submission status and download original files.
 * 
 * Features:
 * - Complete submission information display
 * - Status management with dropdown updates
 * - File download functionality
 * - Detailed part vector search metadata
 * - Navigation back to submissions list
 * 
 * @returns {JSX.Element} The submission detail page component
 */
export function SubmissionDetailPage() {
  const { submissionId } = useParams();
  const [submissionApiCommunicator] = React.useState(new SubmissionApiCommunicator());
  const [loading, setLoading] = React.useState(false);
  const [submission, setSubmission] = React.useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [notes, setNotes] = React.useState("");
  const [savingNotes, setSavingNotes] = React.useState(false);

  const fetchSubmissionData = React.useCallback(async () => {
    try {
      setLoading(true);
      const res = await submissionApiCommunicator.get(submissionId);
      setSubmission(res.data);
      setNotes(res.data.notes || "");
    } catch (err) {
      messageApi.error(err.message);
    }
    setLoading(false);
  }, [submissionId, messageApi, submissionApiCommunicator]);

  React.useEffect(() => {
    fetchSubmissionData();
  }, [fetchSubmissionData]);



  const downloadOriginalFile = React.useCallback(async () => {
    setLoading(true);
    await downloadSubmissionOriginalFile(
      submission,
      (message) => messageApi.info(message),
      (message) => messageApi.success(message),
      (message) => messageApi.error(message)
    );
    setLoading(false);
  }, [submission, messageApi]);

  const updateSubmissionStatus = React.useCallback(async (newStatus) => {
    if (!submission) return;

    try {
      setLoading(true);
      messageApi.info("Updating submission status...");
      
      await submissionApiCommunicator.updateStatus(submission.partSearchSubmissionId, newStatus);
      
      // Refresh the submission data
      await fetchSubmissionData();
      
      messageApi.success("Submission status updated successfully");
    } catch (err) {
      messageApi.error("Error updating submission status: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [submission, submissionApiCommunicator, fetchSubmissionData, messageApi]);

  const saveNotes = React.useCallback(async () => {
    if (!submission) return;

    try {
      setSavingNotes(true);
      messageApi.info("Saving notes...");
      
      await submissionApiCommunicator.update({
        submissionId: submission.partSearchSubmissionId,
        notes: notes
      });
      
      // Refresh the submission data
      await fetchSubmissionData();
      
      messageApi.success("Notes saved successfully");
    } catch (err) {
      messageApi.error("Error saving notes: " + err.message);
    } finally {
      setSavingNotes(false);
    }
  }, [submission, submissionApiCommunicator, notes, fetchSubmissionData, messageApi]);


  if (loading) {
    return (
      <div style={{ margin: "10px" }}>
        <Skeleton active />
      </div>
    );
  }

  if (!submission) {
    return (
      <div style={{ margin: "10px" }}>
        <p>Submission not found</p>
      </div>
    );
  }

  return (
    <div style={{ margin: "10px" }}>
      {contextHolder}
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Link to="/submissions">
            <Button icon={<ArrowLeftOutlined />} type="link">
              Back to Submissions
            </Button>
          </Link>
        </div>
        
        <Card title="Submission Details">
          <Descriptions bordered column={2}>
            <Descriptions.Item label="File Name" span={2}>
              <Space>
                <span>{submission.partVectorSearch?.fileName}</span>
                {submission.partVectorSearch?.files?.original && (
                  <Button
                    type="link"
                    icon={<CloudDownloadOutlined />}
                    onClick={downloadOriginalFile}
                    disabled={loading}
                  >
                    Download File
                  </Button>
                )}
              </Space>
            </Descriptions.Item>
            
            <Descriptions.Item label="Submission Status">
              <Select
                value={submission.status}
                onChange={updateSubmissionStatus}
                disabled={loading}
                style={{ width: 150 }}
                options={getSubmissionStatusOptions()}
              />
            </Descriptions.Item>
            
            <Descriptions.Item label="Part Vector Search Status">
              <Tag color={getStatusColor(submission.partVectorSearch?.status, 'partVectorSearch')}>
                {submission.partVectorSearch?.status?.charAt(0).toUpperCase() + submission.partVectorSearch?.status?.slice(1)}
              </Tag>
            </Descriptions.Item>
            
            <Descriptions.Item label="User ID">
              {submission.userId}
            </Descriptions.Item>
            
            <Descriptions.Item label="Organization ID">
              {submission.orgId}
            </Descriptions.Item>
            
            <Descriptions.Item label="Created" span={2}>
              {formatFullDateFromTimeStamp(submission.createdAt)}
            </Descriptions.Item>
            
            <Descriptions.Item label="Notes" span={2}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <TextArea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this submission..."
                  disabled={loading}
                />
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={saveNotes}
                  loading={savingNotes}
                  disabled={loading}
                >
                  Save Notes
                </Button>
              </Space>
            </Descriptions.Item>
            
            <Descriptions.Item label="Part Vector Search Details" span={2}>
              <Descriptions bordered size="small">
                <Descriptions.Item label="Search ID">
                  {submission.partVectorSearch?.partVectorSearchId}
                </Descriptions.Item>
                <Descriptions.Item label="Company ID">
                  {submission.partVectorSearch?.companyId}
                </Descriptions.Item>
                <Descriptions.Item label="Printer">
                  {submission.partVectorSearch?.printerName || "Not specified"}
                </Descriptions.Item>
                <Descriptions.Item label="Material">
                  {submission.partVectorSearch?.materialName || "Not specified"}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  {formatFullDateFromTimeStamp(submission.partVectorSearch?.createdAt)}
                </Descriptions.Item>
                <Descriptions.Item label="Completed">
                  {submission.partVectorSearch?.completedAt ? formatFullDateFromTimeStamp(submission.partVectorSearch.completedAt) : "Not completed"}
                </Descriptions.Item>
                <Descriptions.Item label="Submitted">
                  {submission.partVectorSearch?.submittedAt ? formatFullDateFromTimeStamp(submission.partVectorSearch.submittedAt) : "Not submitted"}
                </Descriptions.Item>
                <Descriptions.Item label="Started">
                  {submission.partVectorSearch?.startedAt ? formatFullDateFromTimeStamp(submission.partVectorSearch.startedAt) : "Not started"}
                </Descriptions.Item>
                <Descriptions.Item label="PWAM Version">
                  {submission.partVectorSearch?.pwamVersion}
                </Descriptions.Item>
                <Descriptions.Item label="Status Message">
                  {submission.partVectorSearch?.statusMessage || "No status message"}
                </Descriptions.Item>
              </Descriptions>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Space>
    </div>
  );
} 